import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useLang } from "@/context/LangContext";

export function CognitiveScan({ result, onComplete }) {
  const { t, tCheck } = useLang();
  const PHASES = [
    { key: "technical", title: t("scan.technical") },
    { key: "cognitive", title: t("scan.cognitive") },
    { key: "governance", title: t("scan.governance") },
  ];
  const [activePhase, setActivePhase] = useState(0);
  const [revealed, setRevealed] = useState({});

  const checksFor = (key) => result?.[`${key}_checks`] || [];

  useEffect(() => {
    if (!result) return;
    let cancelled = false;
    const run = async () => {
      for (let p = 0; p < PHASES.length; p++) {
        if (cancelled) return;
        setActivePhase(p);
        const checks = checksFor(PHASES[p].key);
        for (let i = 0; i < checks.length; i++) {
          await new Promise((r) => setTimeout(r, 380));
          if (cancelled) return;
          setRevealed((prev) => ({ ...prev, [`${PHASES[p].key}-${i}`]: true }));
        }
        await new Promise((r) => setTimeout(r, 260));
      }
      await new Promise((r) => setTimeout(r, 400));
      if (!cancelled) onComplete?.();
    };
    run();
    return () => { cancelled = true; };
  }, [result]);

  return (
    <div className="relative border border-primary/40 bg-[#0C0D14] p-6 overflow-hidden inner-glow" data-testid="cognitive-scan">
      <motion.div
        className="absolute left-0 right-0 h-[2px] bg-primary neon-glow blur-[1px] pointer-events-none"
        initial={{ top: "0%" }}
        animate={{ top: ["0%", "100%", "0%"] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "linear" }}
      />
      <div className="flex items-center gap-2 mb-5">
        <Loader2 className="w-4 h-4 text-primary animate-spin" />
        <span className="font-display font-bold text-sm text-primary">{t("scan.title")}</span>
      </div>

      <div className="space-y-5">
        {PHASES.map((phase, pIdx) => (
          <div key={phase.key} className={pIdx > activePhase ? "opacity-30" : "opacity-100"}>
            <div className="font-mono text-[11px] tracking-widest text-muted-foreground mb-2">
              {phase.title}
            </div>
            <div className="space-y-1.5">
              {checksFor(phase.key).map((c, i) => {
                const shown = revealed[`${phase.key}-${i}`];
                return (
                  <div key={i} className="flex items-center gap-2 font-mono text-xs">
                    {shown ? (
                      c.passed ? <CheckCircle2 className="w-3.5 h-3.5 text-[#00FF66]" />
                               : <XCircle className="w-3.5 h-3.5 text-[#FF2A2A]" />
                    ) : (
                      <Loader2 className="w-3.5 h-3.5 text-primary/50 animate-spin" />
                    )}
                    <span className={shown ? (c.passed ? "text-foreground" : "text-[#FF2A2A]") : "text-muted-foreground"}>
                      {tCheck(c.name)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
